import React, { useState } from 'react';
import { Button, Table, Select, Modal, Upload, Spin, Card, Typography, Row, Col } from 'antd';
import { UploadOutlined, InfoCircleOutlined, BookOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const TechnicalDrawing = ({ onBack, onInsertSuccess }) => {
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetailsButton, setShowDetailsButton] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isRulesModalVisible, setIsRulesModalVisible] = useState(false);
  const [textractData, setTextractData] = useState(null);

  const demoAssets = [
    { key: '1', code: 'D301', description: 'Vaso de pressão' },
    { key: '2', code: 'PIT03205', description: 'Transmissor de Pressão' },
    { key: '3', code: 'PIT03204', description: 'Transmissor de Pressão' },
    { key: '4', code: 'PIT03206', description: 'Transmissor de Pressão' },
    { key: '5', code: '3"PR0280-0CLP11', description: 'Tubulação' },
  ];

  const plantOptions = [
    { value: 'pp3', label: 'PP3 - Polipropileno' },
    { value: 'pp2', label: 'PP2 - Teste' },
  ];

  const disciplineRules = [
    {
      title: "Elétrica",
      description: "Motores, sistemas elétricos, distribuição de energia, painéis de controle",
      color: "#1890ff",
      rules: [
        { pattern: "^M\\d+", description: "Motor | Elétrica" },
        { pattern: "^MCC\\d+", description: "Centro de Controle de Motor | Elétrica" },
        { pattern: "^E\\d+", description: "Equipamento Elétrico | Elétrica" },
        { pattern: "^CP\\d+", description: "Painel de Controle | Elétrica" }
      ]
    },
    {
      title: "Mecânica",
      description: "Equipamentos rotativos, bombas, compressores, componentes estruturais",
      color: "#52c41a",
      rules: [
        { pattern: "^D\\d+", description: "Vaso de Pressão | Mecânica" },
        { pattern: "^P\\d+", description: "Bomba | Mecânica" },
        { pattern: "^C\\d+", description: "Compressor | Mecânica" },
        { pattern: "^V\\d+", description: "Válvula | Mecânica" }
      ]
    },
    {
      title: "Instrumentação",
      description: "Sensores, dispositivos de medição, sistemas de controle, automação",
      color: "#fa8c16",
      rules: [
        { pattern: "^PIT\\d+", description: "Transmissor de Pressão | Instrumentação" },
        { pattern: "^TIT\\d+", description: "Transmissor de Temperatura | Instrumentação" },
        { pattern: "^FIT\\d+", description: "Transmissor de Vazão | Instrumentação" },
        { pattern: "^LIT\\d+", description: "Transmissor de Nível | Instrumentação" }
      ]
    },
    {
      title: "Tubulação",
      description: "Sistemas de tubulação, incluindo tubulação de processo, utilidades e válvulas associadas",
      color: "#722ed1",
      rules: [
        { pattern: "^\\d\"[A-Za-z0-9]+-[A-Za-z0-9]+", description: "Tubulação | Tubulação" },
        { pattern: "^\\d+\"\\w+", description: "Linha de Tubulação | Tubulação" },
        { pattern: "^PL\\d+", description: "Duto | Tubulação" },
        { pattern: "^\\w+-\\w+-\\w+", description: "Sistema de Tubulação | Tubulação" }
      ]
    }
  ];

  const columns = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
    },
  ];

  const handleUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
      setIsLoading(true);
      
      setTimeout(() => {
        setShowDetailsButton(true);
      }, 4000);
      
      setTimeout(() => {
        setIsLoading(false);
        setShowTable(true);
      }, 6000);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleInsert = () => {
    if (selectedPlant && onInsertSuccess) {
      onInsertSuccess();
    }
  };

  const loadTextractData = async () => {
    try {
      const response = await fetch('/documents/analyzeDocResponse.json');
      const data = await response.json();
      setTextractData(data);
    } catch (error) {
      console.error('Error loading Textract data:', error);
    }
  };

  const handleShowDetails = () => {
    if (!textractData) {
      loadTextractData();
    }
    setIsDetailsModalVisible(true);
  };

  const renderTextractDetails = () => {
    if (!textractData) return <Spin />;

    const lineBlocks = textractData.Blocks
      .filter(block => block.BlockType === 'LINE')
      .sort((a, b) => b.Text.length - a.Text.length);
    
    return (
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <Title level={4}>Documento Analisado</Title>
        <Text type="secondary">Status: {textractData.JobStatus}</Text>
        <br />
        <Text type="secondary">Páginas: {textractData.DocumentMetadata.Pages}</Text>
        
        <Title level={5} style={{ marginTop: 16 }}>Texto Extraído:</Title>
        {lineBlocks.map((block) => (
          <Card key={block.Id} size="small" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>"{block.Text}"</Text>
              <Text type="secondary">Confiança: {block.Confidence?.toFixed(1)}%</Text>
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Posição: Left: {(block.Geometry.BoundingBox.Left * 100).toFixed(1)}%, 
                Top: {(block.Geometry.BoundingBox.Top * 100).toFixed(1)}%
              </Text>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderRulesModal = () => {
    return (
      <div>
        <Title level={4}>Regras de Classificação</Title>
        <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
          Expressões regulares para identificar equipamentos por disciplina
        </Text>
        
        <Row gutter={[16, 16]}>
          {disciplineRules.map((discipline, index) => (
            <Col span={12} key={index}>
              <Card 
                title={discipline.title}
                size="small"
                headStyle={{ 
                  backgroundColor: discipline.color, 
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                style={{ height: '100%' }}
              >
                <Text type="secondary" style={{ fontSize: '12px', marginBottom: 12, display: 'block' }}>
                  {discipline.description}
                </Text>
                
                {discipline.rules.map((rule, ruleIndex) => (
                  <div key={ruleIndex} style={{ marginBottom: 8 }}>
                    <Text code style={{ fontSize: '11px' }}>{rule.pattern}</Text>
                    <br />
                    <Text style={{ fontSize: '12px' }}>{rule.description}</Text>
                  </div>
                ))}
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button onClick={onBack}>
          ← Voltar
        </Button>
        <Upload
          beforeUpload={handleUpload}
          showUploadList={false}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />}>
            Upload Desenho Técnico
          </Button>
        </Upload>
        <Button 
          icon={<BookOutlined />} 
          onClick={() => setIsRulesModalVisible(true)}
          type="default"
        >
          Regras
        </Button>
      </div>

      {uploadedImage && (
        <div style={{ marginBottom: 16 }}>
          <img 
            src={uploadedImage} 
            alt="Uploaded preview" 
            style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'contain' }}
          />
          {showDetailsButton && (
            <div style={{ marginTop: 8 }}>
              <Button 
                icon={<InfoCircleOutlined />} 
                onClick={handleShowDetails}
                type="default"
              >
                Detalhes
              </Button>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Processando imagem...</div>
        </div>
      )}

      {showTable && !isLoading && (
        <>
          <Table
            columns={columns}
            dataSource={demoAssets}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
          
          <div style={{ marginBottom: 16 }}>
            <Select
              placeholder="Selecione uma planta"
              style={{ width: 200, marginRight: 8 }}
              options={plantOptions}
              value={selectedPlant}
              onChange={setSelectedPlant}
            />
            <Button 
              type="primary" 
              onClick={handleInsert}
              disabled={!selectedPlant}
            >
              Inserir
            </Button>
          </div>
        </>
      )}

      <Modal
        title="Análise AWS Textract"
        open={isDetailsModalVisible}
        onOk={() => setIsDetailsModalVisible(false)}
        onCancel={() => setIsDetailsModalVisible(false)}
        width={800}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {renderTextractDetails()}
      </Modal>

      <Modal
        title="Regras de Classificação"
        open={isRulesModalVisible}
        onOk={() => setIsRulesModalVisible(false)}
        onCancel={() => setIsRulesModalVisible(false)}
        width={900}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {renderRulesModal()}
      </Modal>
    </div>
  );
};

export default TechnicalDrawing;